/**
 * ResearchBoardDialog — Public Research Board showing active Earth research
 * projects. All players benefit from completed projects. Players can contribute
 * ISD funding to active projects.
 */

import Phaser from 'phaser';
import { Colors, CSSColors, Fonts, FontSize, Radius, createDialogChrome, closeDialog } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

const PAD = 12;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('crimsonmandate_token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ── Data types ──────────────────────────────────────────────────

interface ResearchProject {
  id: string;
  techName: string;
  tier: number;
  category: string;
  status: 'FUNDING' | 'RESEARCHING' | 'COMPLETED';
  isdFunded: number;
  isdRequired: number;
  mineralPercent: number;
  timeRemainingSeconds: number | null;
  contributorCount: number;
}

interface ResearchBoardData {
  active: ResearchProject[];
  recentlyCompleted: ResearchProject[];
}

// Tier badge colors
const TIER_COLORS: Record<number, { hex: number; css: string }> = {
  1: { hex: 0x888888, css: '#aaaaaa' },
  2: { hex: 0x4488ff, css: '#4488ff' },
  3: { hex: 0xffcc00, css: '#ffcc00' },
  4: { hex: 0xcc66ff, css: '#cc66ff' },
  5: { hex: 0xff8800, css: '#ff8800' },
};

// Status badge colors
const STATUS_COLORS: Record<string, string> = {
  FUNDING:     '#ffcc00',
  RESEARCHING: '#00ffff',
  COMPLETED:   '#00ff88',
};

export class ResearchBoardDialog extends Phaser.GameObjects.Container {
  private contentContainer: Phaser.GameObjects.Container;
  private scrollMask!: Phaser.Display.Masks.GeometryMask;
  private statusText: Phaser.GameObjects.Text;

  // Contribution panel state
  private contributingProjectId: string | null = null;
  private contributeInputValue = '';

  // Data
  private boardData: ResearchBoardData = { active: [], recentlyCompleted: [] };

  // Optional station context
  private stationId?: string;

  // Responsive layout
  private contentW: number;
  private scrollH: number;

  constructor(scene: Phaser.Scene, stationId?: string) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 560;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.contentW = dw - PAD * 2;
    this.scrollH = 430;

    this.stationId = stationId;
    const title = stationId ? 'Station Research' : I18n.t('dialog.research_board');
    const headerDesc = stationId
      ? 'Research projects at this station'
      : 'Research at Earth \u2014 all players benefit from completed projects';

    createDialogChrome(scene, this, dw, dh, {
      title,
      onClose: () => this.close(),
    });

    // Header description
    const headerText = scene.add.text(PAD, 56, headerDesc, {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    this.add(headerText);

    // Scrollable content area
    this.contentContainer = scene.add.container(PAD, 78);
    this.add(this.contentContainer);
    this.setupScrollMask();

    // Status text (bottom center)
    this.statusText = scene.add.text(dw / 2, dh - 15, '', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  private setupScrollMask(): void {
    const maskGraphics = this.scene.add.graphics();
    maskGraphics.fillStyle(0xffffff);
    maskGraphics.fillRect(this.x + PAD, this.y + 78, this.contentW, this.scrollH);
    maskGraphics.setVisible(false);
    this.scrollMask = new Phaser.Display.Masks.GeometryMask(this.scene, maskGraphics);
    this.contentContainer.setMask(this.scrollMask);

    this.scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      const contentH = this.contentContainer.getBounds().height;
      if (contentH <= this.scrollH) return;
      const maxScroll = -(contentH - this.scrollH);
      const localY = this.contentContainer.y - 78;
      const newLocalY = Phaser.Math.Clamp(localY - dy * 0.5, maxScroll, 0);
      this.contentContainer.y = 78 + newLocalY;
    });
  }

  // ============ Data Loading ============

  private async loadData(): Promise<void> {
    this.statusText.setText('Loading...');

    try {
      const url = this.stationId
        ? `/api/research/board?stationId=${encodeURIComponent(this.stationId)}`
        : '/api/research/board';
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.boardData = await res.json();
    } catch {
      this.boardData = { active: [], recentlyCompleted: [] };
    }

    this.statusText.setText(`${this.boardData.active.length} active project(s)`);
    this.renderContent();
  }

  // ============ Rendering ============

  private renderContent(): void {
    this.contentContainer.removeAll(true);
    this.contentContainer.y = 78; // reset scroll
    let y = 0;

    // Active projects
    if (this.boardData.active.length === 0) {
      const empty = this.scene.add.text(this.contentW / 2, 40, 'No active research projects at Earth.', {
        fontSize: FontSize.md, color: '#556677', fontFamily: Fonts.body,
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
      y += 100;
    } else {
      for (const project of this.boardData.active) {
        const row = this.createProjectRow(project, y);
        this.contentContainer.add(row);
        y += 82;

        // If contributing to this project, render the input panel below it
        if (this.contributingProjectId === project.id) {
          const panel = this.createContributePanel(project, y);
          this.contentContainer.add(panel);
          y += 62;
        }
      }
    }

    // Recently completed section
    if (this.boardData.recentlyCompleted.length > 0) {
      y += 8;
      this.addDivider(y); y += 12;

      const sectionHeader = this.scene.add.text(0, y, 'RECENTLY COMPLETED', {
        fontSize: FontSize.xs, color: CSSColors.cyanDim, fontFamily: Fonts.heading,
      });
      this.contentContainer.add(sectionHeader);
      y += 20;

      for (const project of this.boardData.recentlyCompleted.slice(0, 5)) {
        const row = this.createCompletedRow(project, y);
        this.contentContainer.add(row);
        y += 32;
      }
    }
  }

  private createProjectRow(project: ResearchProject, y: number): Phaser.GameObjects.Container {
    const row = this.scene.add.container(0, y);
    const rowH = 76;

    // Row background
    const bg = this.scene.add.graphics();
    bg.fillStyle(Colors.bgCard, 0.9);
    bg.fillRoundedRect(0, 0, this.contentW, rowH, Radius.sm);
    bg.lineStyle(1, Colors.borderDim, 0.15);
    bg.strokeRoundedRect(0, 0, this.contentW, rowH, Radius.sm);
    row.add(bg);

    // Tier badge (colored circle)
    const tierColor = TIER_COLORS[project.tier] || TIER_COLORS[1];
    const tierCircle = this.scene.add.graphics();
    tierCircle.fillStyle(tierColor.hex, 1);
    tierCircle.fillCircle(18, 18, 8);
    tierCircle.lineStyle(1, 0xffffff, 0.15);
    tierCircle.strokeCircle(18, 18, 8);
    row.add(tierCircle);

    const tierLabel = this.scene.add.text(18, 18, `T${project.tier}`, {
      fontSize: '9px', color: '#ffffff', fontFamily: Fonts.heading,
    });
    tierLabel.setOrigin(0.5);
    row.add(tierLabel);

    // Tech name
    const nameText = this.scene.add.text(36, 6, project.techName, {
      fontSize: FontSize.md, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    row.add(nameText);

    // Category label
    const categoryText = this.scene.add.text(36, 24, project.category.toUpperCase(), {
      fontSize: '10px', color: CSSColors.textMuted, fontFamily: Fonts.body,
    });
    row.add(categoryText);

    // Status badge (top right)
    const statusColor = STATUS_COLORS[project.status] || '#888888';
    const statusBadgeBg = this.scene.add.graphics();
    const statusLabel = project.status;
    // Measure text width for badge sizing
    const badgeW = statusLabel.length * 7 + 16;
    statusBadgeBg.fillStyle(parseInt(statusColor.replace('#', ''), 16), 0.15);
    statusBadgeBg.fillRoundedRect(this.contentW - badgeW - 10, 6, badgeW, 18, 3);
    row.add(statusBadgeBg);

    const statusText = this.scene.add.text(this.contentW - 10 - badgeW / 2, 15, statusLabel, {
      fontSize: '10px', color: statusColor, fontFamily: Fonts.heading,
    });
    statusText.setOrigin(0.5);
    row.add(statusText);

    // ISD funding progress bar
    const barX = 36;
    const barY = 42;
    const barW = 280;
    const barH = 12;
    const fundingPct = project.isdRequired > 0 ? Math.min(project.isdFunded / project.isdRequired, 1) : 0;

    const barBg = this.scene.add.graphics();
    barBg.fillStyle(0x0a0f1e, 1);
    barBg.fillRoundedRect(barX, barY, barW, barH, 2);
    row.add(barBg);

    if (fundingPct > 0) {
      const barFill = this.scene.add.graphics();
      barFill.fillStyle(Colors.cyan, 0.8);
      barFill.fillRoundedRect(barX, barY, Math.max(4, barW * fundingPct), barH, 2);
      row.add(barFill);
    }

    const fundingLabel = this.scene.add.text(barX + barW + 8, barY + 1, `$${this.formatNumber(project.isdFunded)} / $${this.formatNumber(project.isdRequired)}`, {
      fontSize: '10px', color: CSSColors.textLight, fontFamily: Fonts.body,
    });
    row.add(fundingLabel);

    // Mineral funding status
    const mineralText = this.scene.add.text(36, 58, `Minerals: ${project.mineralPercent}% funded`, {
      fontSize: '10px', color: CSSColors.textMuted, fontFamily: Fonts.body,
    });
    row.add(mineralText);

    // Time remaining (if researching)
    if (project.status === 'RESEARCHING' && project.timeRemainingSeconds != null) {
      const timeStr = this.formatTime(project.timeRemainingSeconds);
      const timeText = this.scene.add.text(220, 58, `Research: ${timeStr} remaining`, {
        fontSize: '10px', color: CSSColors.cyan, fontFamily: Fonts.body,
      });
      row.add(timeText);
    }

    // Contributor count (bottom right)
    const contribText = this.scene.add.text(this.contentW - 10, 60, `${project.contributorCount} contributor(s)`, {
      fontSize: '10px', color: CSSColors.textMuted, fontFamily: Fonts.body,
    });
    contribText.setOrigin(1, 0);
    row.add(contribText);

    // CONTRIBUTE button (only for FUNDING status)
    if (project.status === 'FUNDING') {
      const btnX = this.contentW - 110;
      const btnY = 34;
      const btnW = 96;
      const btnH = 24;
      const isOpen = this.contributingProjectId === project.id;

      const btnContainer = this.scene.add.container(btnX, btnY);
      const btnBg = this.scene.add.graphics();
      const drawBtn = (hover: boolean) => {
        btnBg.clear();
        const fill = isOpen ? 0x253a66 : hover ? 0x2a4466 : 0x1a3344;
        btnBg.fillStyle(fill, 1);
        btnBg.fillRoundedRect(0, 0, btnW, btnH, Radius.sm);
        btnBg.lineStyle(1, Colors.cyan, isOpen ? 0.4 : hover ? 0.3 : 0.15);
        btnBg.strokeRoundedRect(0, 0, btnW, btnH, Radius.sm);
      };
      drawBtn(false);
      btnContainer.add(btnBg);

      const btnText = this.scene.add.text(btnW / 2, btnH / 2, 'CONTRIBUTE', {
        fontSize: '10px', color: CSSColors.cyan, fontFamily: Fonts.heading,
      });
      btnText.setOrigin(0.5);
      btnContainer.add(btnText);

      const hitZone = this.scene.add.zone(0, 0, btnW, btnH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        drawBtn(true);
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        drawBtn(false);
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', () => {
        if (this.contributingProjectId === project.id) {
          this.contributingProjectId = null;
        } else {
          this.contributingProjectId = project.id;
          this.contributeInputValue = '';
        }
        this.renderContent();
      });
      btnContainer.add(hitZone);
      row.add(btnContainer);
    }

    return row;
  }

  private createContributePanel(project: ResearchProject, y: number): Phaser.GameObjects.Container {
    const panel = this.scene.add.container(0, y);
    const panelH = 52;

    // Panel background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1a2a44, 0.9);
    bg.fillRoundedRect(36, 0, this.contentW - 36, panelH, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.2);
    bg.strokeRoundedRect(36, 0, this.contentW - 36, panelH, Radius.sm);
    panel.add(bg);

    // ISD amount label
    const label = this.scene.add.text(48, 8, 'ISD Amount:', {
      fontSize: FontSize.xs, color: CSSColors.textLight, fontFamily: Fonts.body,
    });
    panel.add(label);

    // Input field background
    const inputX = 148;
    const inputW = 120;
    const inputH = 24;
    const inputBg = this.scene.add.graphics();
    inputBg.fillStyle(0x0a0f1e, 1);
    inputBg.fillRoundedRect(inputX, 5, inputW, inputH, 3);
    inputBg.lineStyle(1, Colors.borderMed, 0.4);
    inputBg.strokeRoundedRect(inputX, 5, inputW, inputH, 3);
    panel.add(inputBg);

    // Input text value
    const inputText = this.scene.add.text(inputX + 6, 10, this.contributeInputValue || '$0', {
      fontSize: FontSize.sm, color: this.contributeInputValue ? CSSColors.gold : CSSColors.textDim, fontFamily: Fonts.body,
    });
    panel.add(inputText);

    // Input hit zone (clickable to type)
    const inputHitZone = this.scene.add.zone(inputX, 5, inputW, inputH).setOrigin(0, 0);
    inputHitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, inputW, inputH), Phaser.Geom.Rectangle.Contains);
    inputHitZone.on('pointerdown', () => {
      // Simple prompt for input (Phaser doesn't have native text inputs)
      const val = window.prompt('Enter ISD amount to contribute:', this.contributeInputValue || '100');
      if (val !== null) {
        const parsed = val.replace(/[^0-9.]/g, '');
        this.contributeInputValue = parsed;
        this.renderContent();
      }
    });
    panel.add(inputHitZone);

    // CONFIRM button
    const confirmX = 286;
    const confirmW = 80;
    const confirmH = 24;
    const confirmContainer = this.scene.add.container(confirmX, 5);

    const confirmBg = this.scene.add.graphics();
    const drawConfirm = (hover: boolean) => {
      confirmBg.clear();
      confirmBg.fillStyle(hover ? 0x2a5544 : 0x1a4422, 1);
      confirmBg.fillRoundedRect(0, 0, confirmW, confirmH, Radius.sm);
      confirmBg.lineStyle(1, Colors.cyan, hover ? 0.3 : 0.15);
      confirmBg.strokeRoundedRect(0, 0, confirmW, confirmH, Radius.sm);
    };
    drawConfirm(false);
    confirmContainer.add(confirmBg);

    const confirmText = this.scene.add.text(confirmW / 2, confirmH / 2, 'CONFIRM', {
      fontSize: '10px', color: CSSColors.green, fontFamily: Fonts.heading,
    });
    confirmText.setOrigin(0.5);
    confirmContainer.add(confirmText);

    const confirmHit = this.scene.add.zone(0, 0, confirmW, confirmH).setOrigin(0, 0);
    confirmHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, confirmW, confirmH), Phaser.Geom.Rectangle.Contains);
    confirmHit.on('pointerover', () => {
      drawConfirm(true);
      this.scene.input.setDefaultCursor('pointer');
    });
    confirmHit.on('pointerout', () => {
      drawConfirm(false);
      this.scene.input.setDefaultCursor('default');
    });
    confirmHit.on('pointerdown', () => {
      this.submitContribution(project.id);
    });
    confirmContainer.add(confirmHit);
    panel.add(confirmContainer);

    // Help text
    const helpText = this.scene.add.text(48, 34, 'Enter an ISD amount and click CONFIRM to fund this project.', {
      fontSize: '10px', color: CSSColors.textMuted, fontFamily: Fonts.body,
    });
    panel.add(helpText);

    return panel;
  }

  private createCompletedRow(project: ResearchProject, y: number): Phaser.GameObjects.Container {
    const row = this.scene.add.container(0, y);
    const rowH = 28;

    // Subtle background
    const bg = this.scene.add.graphics();
    bg.fillStyle(Colors.bgCard, 0.5);
    bg.fillRoundedRect(0, 0, this.contentW, rowH, Radius.sm);
    row.add(bg);

    // Tier dot
    const tierColor = TIER_COLORS[project.tier] || TIER_COLORS[1];
    const dot = this.scene.add.graphics();
    dot.fillStyle(tierColor.hex, 0.8);
    dot.fillCircle(14, rowH / 2, 4);
    row.add(dot);

    // Tech name
    const nameText = this.scene.add.text(26, rowH / 2, project.techName, {
      fontSize: FontSize.xs, color: CSSColors.textLight, fontFamily: Fonts.body,
    });
    nameText.setOrigin(0, 0.5);
    row.add(nameText);

    // Category
    const catText = this.scene.add.text(260, rowH / 2, project.category.toUpperCase(), {
      fontSize: '10px', color: CSSColors.textMuted, fontFamily: Fonts.body,
    });
    catText.setOrigin(0, 0.5);
    row.add(catText);

    // Completed badge
    const completedText = this.scene.add.text(this.contentW - 10, rowH / 2, 'COMPLETED', {
      fontSize: '10px', color: CSSColors.green, fontFamily: Fonts.heading,
    });
    completedText.setOrigin(1, 0.5);
    row.add(completedText);

    return row;
  }

  // ============ Contribution ============

  private async submitContribution(projectId: string): Promise<void> {
    const amount = parseFloat(this.contributeInputValue);
    if (isNaN(amount) || amount <= 0) {
      this.statusText.setText('Invalid amount');
      return;
    }

    this.statusText.setText('Submitting...');

    try {
      const res = await fetch('/api/research/contribute', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ projectId, amount }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        this.statusText.setText(err.error || 'Contribution failed');
        return;
      }
      this.contributingProjectId = null;
      this.contributeInputValue = '';
      this.statusText.setText('Contribution successful!');
      this.loadData(); // refresh
    } catch {
      this.statusText.setText('Network error');
    }
  }

  // ============ Helpers ============

  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.08);
    g.lineBetween(0, y, this.contentW, y);
    this.contentContainer.add(g);
  }

  private formatNumber(n: number): string {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
}
